// authorizationDrawerBridge — a minimal module-level singleton that lets
// non-React modules (the action-proposal executor registry in `builtinActions.ts`)
// drive the shell-mounted `AuthorizationDrawer` imperatively.
//
// Mirrors `sourceWizardBridge`: the registry runs at import time and is a plain
// module, so it cannot call the `useAuthorizationDrawer()` hook.
// `AuthorizationDrawerProvider` publishes its imperative opener here on mount;
// `builtinActions.ts` calls `openAuthorizationDrawer(...)`, which forwards to the
// published opener (or rejects with a clear "not mounted" error if the provider
// isn't in the tree).
//
// Used by the assistant reconnect handoff (plan 2026-07-29 assistant-reconnect-
// flow, steps 2 + 5): confirming a reconnect card opens the drawer pre-loaded
// for the paused source's authorization, where the user completes the reconnect
// (OAuth re-consent or a new API key). On a successful reconnect the paused
// source auto-resumes server-side.

/**
 * Resolves `{ reconnected: true }` when the drawer signalled a successful change
 * (reconnect / key rotation) during the open, or `null` when the drawer was
 * closed without one.
 */
export type AuthorizationDrawerResult = { reconnected: boolean } | null;

export type OpenAuthorizationDrawer = (
  authorizationPublicId: string,
) => Promise<AuthorizationDrawerResult>;

let opener: OpenAuthorizationDrawer | null = null;

/** Called by `AuthorizationDrawerProvider` on mount (and with `null` on unmount). */
export function setAuthorizationDrawerOpener(
  fn: OpenAuthorizationDrawer | null,
): void {
  opener = fn;
}

/**
 * Open the shell-mounted `AuthorizationDrawer` pre-loaded for the authorization
 * with the given PUBLIC id (`auth_...`) and resolve with whether the user
 * completed a reconnect (`{ reconnected: true }`) or closed without one
 * (`null`). Rejects if no provider is mounted so a mis-wired build fails loudly
 * instead of silently no-opping a confirm click.
 */
export function openAuthorizationDrawer(
  authorizationPublicId: string,
): Promise<AuthorizationDrawerResult> {
  if (!opener) {
    return Promise.reject(
      new Error(
        "Authorization drawer is not available (AuthorizationDrawerProvider is not mounted).",
      ),
    );
  }
  return opener(authorizationPublicId);
}
